/* eslint-disable max-lines */
import type { HttpErrorResponse } from '@angular/common/http';
import type { ViewContainerRef } from '@angular/core';
import { inject, Injectable } from '@angular/core';
import type { Params, UrlTree } from '@angular/router';
import { Router } from '@angular/router';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import type { Observable, OperatorFunction } from 'rxjs';
import { combineLatest, from, merge, of, race, throwError } from 'rxjs';
import {
  catchError,
  delay,
  filter,
  map,
  pluck,
  skip,
  startWith,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';

import { Ability } from '@core/abilities-types';
import { CurrentUserEntityActions, UserSelectors } from '@core/account-data';
import type { User } from '@core/account-types';
import type {
  Addon,
  BillingAddressFormFields,
  BillingEstimate,
  BillingInformation,
  BillingInformationValidationError,
  BillingPlan,
  CancelPayload,
  NextPaymentInfoTax,
  PaymentIntent,
  PaymentInterval,
} from '@core/billing-types';
import { Currency } from '@core/billing-types';
import { filterBoolean, firstPayload } from '@core/common-legacy';
import { I18nService } from '@core/i18n-legacy';
import { IntercomService } from '@core/intercom-integration-legacy';
import {
  InvoiceDownloadUrlService,
  InvoicesCollectionActions,
  InvoicesCollectionSelectors,
} from '@core/invoices-data';
import type { InvoiceDownloadURL, InvoiceResourceType, InvoiceType } from '@core/invoices-types';
import { DialogService, DialogWidth, NotificationsController } from '@core/user-interface-legacy';
import {
  BillingEstimateActions,
  BillingEstimateSelectors,
  BillingPlansActions,
  BillingPlansSelectors,
  SubscriptionPricingActions,
} from '@recruitee/billing-data';
import { BillingInformationEntityActions } from '@recruitee/billing-information-data';
import { Helpers } from '@recruitee/billing-logic';
import type {
  BillingContactFormFields,
  JobSlotsDialogModel,
  NextPaymentInfoUI,
  PaymentMethodInfo,
  PricingFeatureLoadedModel,
  ProductOrderConfig,
  StripeElements,
  VatTaxUI,
} from '@recruitee/billing-smart-ui';
import {
  JobSlotsDialogComponent,
  PricingFeatureFacade,
  ProductOrderDefaultStep,
  StripeService,
} from '@recruitee/billing-smart-ui';
import { SampleDataService } from '@recruitee/sample-data-logic';

@Injectable({ providedIn: 'root' })
export class PricingFeatureConnectedFacade extends PricingFeatureFacade {
  private readonly notify = inject(NotificationsController);
  private readonly router = inject(Router);
  private readonly dialogService = inject(DialogService);
  private readonly stripeService = inject(StripeService);
  private readonly i18n = inject(I18nService);
  private readonly sampleDataService = inject(SampleDataService);
  private readonly intercom = inject(IntercomService);
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly invoiceDownloadUrl = inject(InvoiceDownloadUrlService);

  constructor() {
    super();
  }

  public deleteCreditCard() {
    this.store.dispatch(SubscriptionPricingActions.deleteCard());
    return this.actions$.pipe(
      ofType(SubscriptionPricingActions.deleteCardSuccess),
      firstPayload,
      tap(() =>
        this.notify.success(this.i18n.translate('app.pricing.billing_page.delete_card_success')),
      ),
      catchError(err => {
        const msg = err && err.error && err.error.error[0];
        this.notify.error(msg);
        return of(false);
      }),
    );
  }

  public orderCompletedSuccessfully(hasSampleData: boolean, showPlanChangeMessage: boolean = true) {
    this.router.navigateByUrl(this.getBillingPageUrl().toString(), {
      replaceUrl: true,
      state: { showPlanChangeMessage, isSampledDataRemoved: hasSampleData },
    });
  }

  public getBillingPageUrlFromModel(model: PricingFeatureLoadedModel): UrlTree | null {
    if (model.trial || model.subscriptionStatus.inactive || model.subscriptionStatus.trialing) {
      if (model.trial?.expired || model.subscriptionStatus.inactive) {
        return this.getEndedTrialUrl();
      }

      return this.getActiveTrialUrl();
    }

    if (model.subscriptionStatus.blocked) {
      if (model.canManageSubscription) {
        return this.getBillingPageUrl();
      } else {
        return this.getSubscriptionBlockedUrl();
      }
    }

    if (model.subscription) {
      if (model.canManageSubscription) {
        return this.getBillingPageUrl();
      } else {
        return this.getLimitedAccessUrl();
      }
    }

    return null;
  }

  public canAccessBillingPage(model: PricingFeatureLoadedModel): boolean {
    return (
      this.getBillingPageUrlFromModel(model)?.toString() === this.getBillingPageUrl().toString()
    );
  }

  public openSlotsPickerDialog(
    product: BillingPlan,
    model: PricingFeatureLoadedModel,
    manual: boolean,
    vcRef: ViewContainerRef,
  ) {
    this.dialogService.openCustom(
      JobSlotsDialogComponent,
      {
        width: model.subscription?.manual ? DialogWidth.SmallX : DialogWidth.SmallXX,
      },
      <JobSlotsDialogModel>{
        product,
        model,
      },
      vcRef,
    );
  }

  public changePlan(
    productId: number,
    slots: number,
    interval: PaymentInterval,
  ): Observable<boolean> {
    this.store.dispatch(
      SubscriptionPricingActions.changePlan({ payload: { productId, slots, interval } }),
    );
    return this.actions$.pipe(
      ofType(SubscriptionPricingActions.changePlanSuccess),
      firstPayload,
      this.payment(),
    );
  }

  public paymentIntent(
    billingPlanId: number,
    billingPeriod: PaymentInterval,
    addons: Addon[],
    sizeRange?: string,
  ): Observable<any> {
    this.store.dispatch(
      SubscriptionPricingActions.paymentIntent({
        payload: {
          billingPeriod,
          billingPlanId,
          addons,
          ...(sizeRange && { fteRange: sizeRange }),
        },
      }),
    );
    const successAction$ = this.actions$.pipe(
      ofType(SubscriptionPricingActions.paymentIntentSuccess),
      firstPayload,
      pluck('paymentIntent'),
      this.newPayment({
        billingPlanId,
        billingPeriod,
        addons,
        ...(sizeRange && { fteRange: sizeRange }),
      }),
    );
    const failedAction$ = this.actions$.pipe(
      ofType(SubscriptionPricingActions.paymentIntentFailure),
      switchMap(err => throwError(err)),
    );
    return race(successAction$, failedAction$);
  }

  public patchSubscription(
    billingPlanId: number,
    billingPeriod: PaymentInterval,
    addons: Addon[],
    paymentIntentId?: string | null,
    fteRange?: string,
  ): Observable<any> {
    this.store.dispatch(
      SubscriptionPricingActions.patchSubscription({
        payload: {
          billingPlanId,
          billingPeriod,
          addons,
          paymentIntentId: paymentIntentId ? paymentIntentId : null,
          ...(fteRange && { fteRange }),
        },
      }),
    );

    return race(
      this.actions$.pipe(ofType(SubscriptionPricingActions.patchSubscriptionSuccess)),
      this.actions$
        .pipe(ofType(SubscriptionPricingActions.patchSubscriptionFailure))
        .pipe(switchMap(({ payload }) => throwError(payload.error))),
    ).pipe(take(1));
  }

  public reactivatePlan(): Observable<boolean> {
    this.store.dispatch(SubscriptionPricingActions.reactivate());
    return this.actions$.pipe(
      ofType(SubscriptionPricingActions.reactivateSuccess),
      take(1),
      map(() => true),
    );
  }

  public changeSlots(slots: number, currentSlots: number): Observable<boolean> {
    this.store.dispatch(SubscriptionPricingActions.changeSlots({ payload: slots }));
    return this.actions$.pipe(
      ofType(SubscriptionPricingActions.changeSlotsSuccess),
      firstPayload,
      this.payment(),
      tap(() => {
        this.notify.success(
          this.i18n.translate(
            currentSlots < slots
              ? 'app.pricing.job_slots_increased'
              : 'app.pricing.job_slots_decreased',
          ),
        );
      }),
    );
  }

  public newChangeSlots(
    billingPlanId: number,
    billingPeriod: PaymentInterval,
    slotsIncreased: boolean,
    addons: Addon[],
    isWithPaymentIntent: boolean,
  ): Observable<boolean> {
    const paymentIntent$ = isWithPaymentIntent
      ? this.paymentIntent(billingPlanId, <1 | 12>billingPeriod, addons)
      : of(null).pipe(this.newPayment({ billingPlanId, billingPeriod, addons }));
    return paymentIntent$.pipe(
      catchError(err => throwError(err)),
      tap(() => {
        this.notify.success(
          this.i18n.translate(
            slotsIncreased ? 'app.pricing.job_slots_increased' : 'app.pricing.job_slots_decreased',
          ),
        );
      }),
    );
  }

  public changeAddons(
    billingPlanId: number,
    billingPeriod: PaymentInterval,
    addons: Addon[],
    skipPaymentIntent: boolean,
  ): Observable<boolean> {
    const paymentIntent$ = skipPaymentIntent
      ? of(null).pipe(this.newPayment({ billingPlanId, billingPeriod, addons }))
      : this.paymentIntent(billingPlanId, <1 | 12>billingPeriod, addons);
    return paymentIntent$.pipe(catchError(err => throwError(err)));
  }

  public getInvoices(): Observable<InvoiceType[]> {
    this.store.dispatch(
      InvoicesCollectionActions.fetchPaginatedCollection({
        payload: { params: { limit: 30, page: 1 }, scope: 'main' },
      }),
    );
    return this.store.select(InvoicesCollectionSelectors.entities());
  }

  public loadMoreInvoices() {
    this.store
      .select(InvoicesCollectionSelectors.pagination({ scope: 'main' }))
      .pipe(take(1))
      .subscribe(pagination => {
        this.store.dispatch(
          InvoicesCollectionActions.fetchPaginatedCollection({
            payload: {
              params: { limit: pagination.limit!, page: (pagination.page || 1) + 1 },
              scope: 'main',
              add: true,
            },
          }),
        );
      });
  }

  public downloadInvoice(invoiceId: string, resourceType: InvoiceResourceType) {
    return this.getInvoiceDownloadUrl(invoiceId, resourceType).subscribe(downloadUrl =>
      window.open(downloadUrl.pdfUrl),
    );
  }

  public getNextPaymentInfo(): Observable<NextPaymentInfoUI | null> {
    this.store.dispatch(SubscriptionPricingActions.loadNextPayment());
    const data$ = this.actions$.pipe(
      ofType(SubscriptionPricingActions.loadNextPaymentSuccess),
      firstPayload,
      map(nextPayment => {
        if (!nextPayment) return nextPayment;
        return {
          addons: nextPayment.addons,
          total: nextPayment.total / 100,
          amountDue: nextPayment.amountDue / 100,
          creditsApplied: nextPayment.creditsApplied / 100,
          discounts: nextPayment.discounts / 100,
          date: nextPayment.date,
          subTotal: nextPayment.subTotal / 100,
          currency: nextPayment.currencyCode!,
          paysVat: nextPayment.paysVat,
          taxes: nextPayment.taxes || [],
          vatTaxUI: nextPayment.paysVat ? this.mapVatTaxUI(nextPayment.taxes || []) : null,
        };
      }),
      catchError(_ => of(null)),
    );

    return merge(
      this.actions$.pipe(ofType(CurrentUserEntityActions.fetchSuccess)).pipe(
        switchMap(() => data$),
        skip(1),
      ),
      data$,
    );
  }

  public getBillingPlans() {
    return this.store.select(BillingPlansSelectors.billingPlans).pipe(filterBoolean);
  }

  public getActiveBillingPlan(): Observable<BillingPlan | undefined> {
    return this.store.select(BillingPlansSelectors.activePlan);
  }

  public getBillingPlansWithActive(): Observable<BillingPlan[]> {
    return combineLatest([this.getBillingPlans(), this.getActiveBillingPlan()]).pipe(
      map(plans => <BillingPlan[]>plans.flat().filter(Boolean)),
    );
  }

  public getBillingPlanById(id: number): Observable<BillingPlan> {
    return combineLatest([this.getBillingPlans(), this.getActiveBillingPlan()]).pipe(
      switchMap(([products, activeProduct]) => {
        if (activeProduct && activeProduct.id === id) return of(activeProduct);

        const product = products.find(p => p.id === id);
        return product ? of(product) : throwError(`BillingPlan with id: "${id}" not found`);
      }),
    );
  }

  public getLimitedAccessUrl(): UrlTree {
    return this.removeDialogOutlet(
      this.router.createUrlTree(['settings', 'pricing', 'limited-access']),
    );
  }

  public getBillingPageUrl(): UrlTree {
    return this.removeDialogOutlet(this.router.createUrlTree(['settings', 'pricing', 'billing']));
  }

  public getActiveTrialUrl(): UrlTree {
    return this.removeDialogOutlet(
      this.router.createUrlTree(['settings', 'pricing', 'trial', 'active']),
    );
  }

  public getEndedTrialUrl(): UrlTree {
    return this.removeDialogOutlet(
      this.router.createUrlTree(['settings', 'pricing', 'trial', 'end']),
    );
  }

  public getSubscriptionExpiredUrl(): UrlTree {
    return this.removeDialogOutlet(
      this.router.createUrlTree(['settings', 'pricing', 'subscription', 'expired']),
    );
  }

  public getSubscriptionBlockedUrl(): UrlTree {
    return this.removeDialogOutlet(
      this.router.createUrlTree(['settings', 'pricing', 'subscription', 'blocked']),
    );
  }

  public getSubscriptionChurnPageUrl(): UrlTree {
    return this.removeDialogOutlet(
      this.router.createUrlTree(['settings', 'pricing', 'subscription', 'cancel']),
    );
  }

  public getAdminsWithManageSubscriptionAbility(): Observable<User[]> {
    const adminsLeft = 3;

    return this.store.select(UserSelectors.entities()).pipe(
      filterBoolean,
      map(teamMembers =>
        teamMembers
          .filter(member => (member.roleAbilities || []).includes(Ability.ManageSubscription))
          .slice(0, adminsLeft),
      ),
      startWith([]),
    );
  }

  public getPlanPickerUrl(config?: { legacyPlans: boolean }): UrlTree {
    const dialogPath = config?.legacyPlans ? 'order' : 'order-2024';
    return this.router.createUrlTree(
      [
        {
          outlets: { dialog: [dialogPath, ProductOrderDefaultStep] },
        },
      ],
      { queryParamsHandling: 'preserve' },
    );
  }

  public openPlanPicker(
    config: ProductOrderConfig = { skipRedirectOnSuccess: false },
  ): Promise<boolean> {
    const queryParams: Params = {};

    if (config.skipRedirectOnSuccess) queryParams['skipRedirectOnSuccess'] = true;

    const dialogPath = config?.legacyPlans ? 'order' : 'order-2024';

    return this.router.navigate(
      [
        {
          outlets: { dialog: [dialogPath, ProductOrderDefaultStep] },
        },
      ],
      { queryParams },
    );
  }

  public openBilling(
    productId: number,
    slots: number,
    interval?: PaymentInterval,
    downgradeScreenViewed?: boolean,
  ) {
    return this.router.navigate(
      [
        {
          outlets: {
            dialog: ['order', productId, slots, interval || 12, 'billing'],
          },
        },
      ],
      {
        state: {
          downgradeScreenViewed,
        },
      },
    );
  }

  public openBilling2024(
    productId: number,
    quantity?: number,
    interval?: PaymentInterval,
    downgradeScreenViewed?: boolean,
  ) {
    return this.router.navigate(
      [
        {
          outlets: {
            dialog: ['order-2024', productId, quantity, interval || 12, 'billing'],
          },
        },
      ],
      {
        state: {
          downgradeScreenViewed,
        },
      },
    );
  }

  public cancelSubscription(data: CancelPayload): Observable<boolean> {
    this.store.dispatch(SubscriptionPricingActions.cancel({ payload: data }));
    return this.actions$.pipe(
      ofType(SubscriptionPricingActions.cancelSuccess),
      take(1),
      map(_ => true),
    );
  }

  public updatePaymentMethodData(
    data: PaymentMethodInfo,
    stripeElements: StripeElements,
    genericErrorHandler = true,
  ): Observable<boolean> {
    return from(
      this.stripeService.createToken(stripeElements.card, {
        name: stripeElements.name,
      }),
    ).pipe(
      switchMap(res => {
        if (res) {
          if (res.token && res.token.id) {
            return of(res.token.id);
          }

          if (res.error) {
            return throwError(res.error);
          }
        }

        return throwError({ message: 'Unable to create payment token.' });
      }),
      switchMap((stripeToken: string) =>
        this.addCreditCard(
          stripeToken,
          null,
          this.i18n.translate('app.pricing.payment_method_dialog.save_fail'),
          genericErrorHandler,
        ),
      ),
    );
  }

  public updateBillingAddressData(data: BillingAddressFormFields): Observable<boolean> {
    return this.updateBillingInformation(
      data,
      this.i18n.translate('app.pricing.billing_address_form.save_ok'),
      this.i18n.translate('app.pricing.billing_address_form.save_fail'),
      true,
    );
  }

  public updateBillingContactData(data: BillingContactFormFields): Observable<boolean> {
    return this.updateBillingInformation(
      data,
      this.i18n.translate('app.pricing.billing_contact_form.save_ok'),
      this.i18n.translate('app.pricing.billing_contact_form.save_fail'),
      true,
    );
  }

  public updateBillingData(data: Partial<BillingInformation>): Observable<boolean> {
    return this.updateBillingInformation(data, undefined, undefined, false);
  }

  public hasSampleData(): Observable<boolean> {
    return this.sampleDataService.hasSampleData$;
  }

  public openIntercom(): void {
    this.intercom.open();
  }

  // eslint-disable-next-line max-params-no-constructor/max-params-no-constructor
  public changeCurrency(
    currency: Currency,
    product: BillingPlan,
    model: PricingFeatureLoadedModel,
    interval: PaymentInterval,
    slots?: number,
    quantity?: number,
  ) {
    if (!slots && !quantity) {
      throw new Error('Either slots or quantity is required');
    }
    this.getBillingPlans()
      .pipe(take(1))
      .subscribe(billingPlans => {
        const newBillingPlan = billingPlans.find(
          billingPlan =>
            billingPlan.currency === currency && billingPlan.tier === (<BillingPlan>product).tier,
        )!;
        model.changeCurrency(Currency[currency]);
        if (slots) {
          this.openBilling(newBillingPlan.id, slots, interval, true);
        } else {
          this.openBilling2024(newBillingPlan.id, quantity, interval, true);
        }
        model.changeCurrency(Currency[currency]);
        this.notify.success(
          this.i18n.translate(
            'app.pricing.billing_address_form.currency_change_dialog.successMessage',
          ),
        );
      });
  }

  public loadBillingEstimate(
    billingPeriod: PaymentInterval,
    billingPlanId: number,
    addons: Addon[],
    sizeRange?: string,
  ): Observable<BillingEstimate> {
    this.store.dispatch(
      BillingEstimateActions.fetch({
        payload: {
          billingPeriod: <number>billingPeriod,
          billingPlanId,
          addons,
          ...(sizeRange && { fteRange: sizeRange }),
        },
      }),
    );
    return this.store.select(BillingEstimateSelectors.data).pipe(filterBoolean);
  }

  public fetchPlansIfNeeded() {
    combineLatest([
      this.store.select(BillingPlansSelectors.isLoaded),
      this.store.select(BillingPlansSelectors.isFailed),
    ])
      .pipe(
        take(1),
        filter(([isLoaded, isFailed]) => !isLoaded && !isFailed),
      )
      .subscribe(() => {
        this.store.dispatch(BillingPlansActions.fetchActivePlan());
        this.store.dispatch(BillingPlansActions.fetch());
      });
  }

  private updateBillingInformation(
    data: BillingAddressFormFields | BillingContactFormFields | PaymentMethodInfo,
    genericOkMsg?: string | null,
    genericFailMsg?: string,
    genericErrorHandler = false,
  ): Observable<boolean> {
    this.store.dispatch(
      BillingInformationEntityActions.updateBillingInformation({ payload: { updateData: data } }),
    );
    const updateSuccess$ = this.actions$.pipe(ofType(CurrentUserEntityActions.fetchSuccess));
    const updateFailure$ = this.actions$.pipe(
      ofType(BillingInformationEntityActions.updateBillingInformationFailed),
    );
    return race(updateSuccess$, updateFailure$).pipe(
      take(1),
      switchMap(successOrFailureAction => {
        if (
          successOrFailureAction.type ===
          BillingInformationEntityActions.updateBillingInformationFailed.type
        ) {
          return this.handleBillingInformationFailedAction(
            successOrFailureAction.payload,
            genericFailMsg,
            genericErrorHandler,
          );
        }

        return of(true);
      }),
      tap(value => {
        if (value && genericOkMsg) {
          this.notify.success(genericOkMsg);
        }
      }),
    );
  }

  private handleBillingInformationFailedAction(
    error: HttpErrorResponse,
    genericFailMsg?: string,
    genericErrorHandler = false,
  ): Observable<boolean> {
    const apiBillingError: BillingInformationValidationError =
      Helpers.httpToBillingInformationValidationError(error, genericFailMsg);

    if (genericErrorHandler) {
      this.notify.error(apiBillingError.errorDescription);
      return of(false);
    }

    return throwError({ message: error.error });
  }

  private addCreditCard(
    stripeToken: string,
    genericOkMsg?: string | null,
    genericFailMsg?: string,
    genericErrorHandler = false,
  ): Observable<boolean> {
    this.store.dispatch(SubscriptionPricingActions.addCard({ payload: stripeToken }));
    return <Observable<boolean>>merge(
      this.actions$.pipe(
        ofType(SubscriptionPricingActions.addCardSuccess),
        take(1),
        switchMap(() => this.actions$.pipe(ofType(CurrentUserEntityActions.fetchSuccess))),
        tap(() => (genericOkMsg ? this.notify.success(genericOkMsg) : null)),
        map(_ => true),
      ),
      this.actions$.pipe(
        ofType(SubscriptionPricingActions.addCardFailure),
        switchMap(({ payload }) => throwError(payload.error)),
      ),
    ).pipe(
      take(1),
      catchError(err => {
        const message = (err && err.error) || genericFailMsg;
        if (genericErrorHandler) {
          this.notify.error(message.errorDescription || '');
          return of(false);
        } else {
          return throwError({ message });
        }
      }),
    );
  }

  private removeDialogOutlet(urlTree: UrlTree) {
    delete urlTree.root.children.dialog;
    return urlTree;
  }

  private payment(): OperatorFunction<{ paymentIntent: PaymentIntent | null }, boolean> {
    const flowWithoutPaymentIntent = () => of(true);
    const flowWithPaymentIntent = (intent: PaymentIntent) =>
      from(this.stripeService.handleCardPayment(intent.clientSecret)).pipe(
        switchMap(response => {
          const error =
            response.error &&
            response.error.code === 'payment_intent_incompatible_payment_method' &&
            response.error.payment_intent
              ? response.error.payment_intent.last_payment_error
              : response.error;
          return error ? throwError(error) : of(true);
        }),
        delay(1000),
      );

    return (source: Observable<{ paymentIntent: PaymentIntent }>) =>
      source.pipe(
        catchError(err => throwError({ message: err && err.error })),
        switchMap(body =>
          body.paymentIntent === null || body.paymentIntent.status === 'succeeded'
            ? flowWithoutPaymentIntent()
            : flowWithPaymentIntent(body.paymentIntent),
        ),
        tap(() => this.store.dispatch(CurrentUserEntityActions.fetch())),
        switchMap(() => this.actions$.pipe(ofType(CurrentUserEntityActions.fetchSuccess))),
        map(() => true),
        take(1),
      );
  }

  private newPayment(params: {
    billingPlanId: number;
    billingPeriod: PaymentInterval;
    addons: Addon[];
    fteRange?: string;
  }): OperatorFunction<PaymentIntent | null, boolean> {
    const flowWithoutPaymentIntent = (paymentIntent: PaymentIntent) => of(paymentIntent);
    const flowWithPaymentIntent = (paymentIntent: PaymentIntent) =>
      from(this.stripeService.handleCardAction(paymentIntent.clientSecret)).pipe(
        switchMap(response => {
          if (!response.error) return of(response.paymentIntent);

          return this.handleStripeCardActionError(response.error);
        }),
        delay(1000),
      );

    return (source: Observable<PaymentIntent>) =>
      source.pipe(
        catchError(err => throwError({ message: err && err.error })),
        switchMap(paymentIntent =>
          !!paymentIntent && paymentIntent.status === 'requires_action'
            ? flowWithPaymentIntent(paymentIntent)
            : flowWithoutPaymentIntent(paymentIntent),
        ),
        switchMap((paymentIntent: PaymentIntent) =>
          this.patchSubscription(
            params.billingPlanId,
            params.billingPeriod,
            params.addons,
            paymentIntent?.id,
            params.fteRange,
          ),
        ),
        tap(() => this.store.dispatch(BillingPlansActions.fetchActivePlan())),
        tap(() => this.store.dispatch(CurrentUserEntityActions.fetch())),
        switchMap(() => this.actions$.pipe(ofType(CurrentUserEntityActions.fetchSuccess))),
        map(() => true),
        take(1),
      );
  }

  private getInvoiceDownloadUrl(
    invoiceId: string,
    resourceType: InvoiceResourceType,
  ): Observable<InvoiceDownloadURL> {
    return this.invoiceDownloadUrl.get(invoiceId, resourceType);
  }

  private handleStripeCardActionError(error: any) {
    this.store.dispatch(
      SubscriptionPricingActions.paymentIntentStripeFailure({
        payload: { error },
      }),
    );
    return throwError(error);
  }

  private mapVatTaxUI(taxes: NextPaymentInfoTax[]): VatTaxUI | null {
    const vat = taxes.find(tax => tax.name === 'VAT');
    if (!vat) return null;
    // vat.description: "VAT @ 20 %"
    const regex = /\d+(?:\.\d+)?/;
    const match = vat.description.match(regex);
    if (match?.length) {
      return {
        name: vat.name,
        rate: `${match[0]}%`,
      };
    }
    return null;
  }
}
